/**
 * Pixels per wheel tick when the browser reports lines instead of pixels. Chrome and
 * Safari report pixels; Firefox on some platforms reports lines.
 */
const LINE_HEIGHT = 16;

/**
 * The element inside the column that a wheel over the grey should move.
 *
 * The app's screens each have their own ScrollView, so the target changes per screen.
 * Picks the one with the most left to scroll, which is the screen's main list rather
 * than a small horizontal strip inside it.
 */
export function scrollTargetIn(column: HTMLElement): HTMLElement | null {
	let best: HTMLElement | null = null;
	let bestRoom = 0;

	for (const element of column.querySelectorAll<HTMLElement>('*')) {
		const room = element.scrollHeight - element.clientHeight;
		if (room <= bestRoom) continue;

		const overflow = getComputedStyle(element).overflowY;
		if (overflow !== 'auto' && overflow !== 'scroll') continue;

		best = element;
		bestRoom = room;
	}

	return best;
}

/**
 * The last target found, so a wheel tick does not sweep the column again.
 *
 * scrollTargetIn walks every descendant and reads its box, which is a layout read per node, and a
 * wheel gives dozens of events per second. The screen only changes on navigation, so the answer is
 * reused until it stops being usable -- which is exactly what happens when a screen unmounts.
 */
let cachedColumn: HTMLElement | null = null;
let cachedScroller: HTMLElement | null = null;

/** Cheap check that the remembered element is still the one to scroll: three reads, not hundreds. */
function stillUsable(scroller: HTMLElement | null, column: HTMLElement): HTMLElement | null {
	if (scroller === null || !column.contains(scroller)) return null;

	return scroller.scrollHeight - scroller.clientHeight > 0 ? scroller : null;
}

function pixelsFor(event: WheelEvent): number {
	if (event.deltaMode === 1) return event.deltaY * LINE_HEIGHT;
	if (event.deltaMode === 2) return event.deltaY * window.innerHeight;

	return event.deltaY;
}

/**
 * Scrolls the column when the pointer is on the grey beside it.
 *
 * On a wide screen the app is a 500px column and the rest of the window is background.
 * That background has no scrollable ancestor, so a wheel over it scrolled nothing at
 * all -- the page looked frozen unless you happened to aim at the column. Returns
 * whether it handled the event.
 */
export function forwardWheel(event: WheelEvent, column: HTMLElement | null): boolean {
	if (!column) return false;

	// Inside the column the browser already does the right thing, and stepping in would
	// scroll twice as fast.
	const target = event.target as Node | null;
	if (target !== null && column.contains(target)) return false;

	let scroller = cachedColumn === column ? stillUsable(cachedScroller, column) : null;

	if (scroller === null) {
		// Only on the first tick after a screen change. Within one screen the remembered element
		// keeps winning: it had the most room, and it is only dropped once it has none left.
		scroller = scrollTargetIn(column);
		cachedColumn = column;
		cachedScroller = scroller;
	}

	if (!scroller) return false;

	scroller.scrollTop += pixelsFor(event);

	return true;
}
